/**
 * CSV upload component with drag-and-drop.
 * Accepts monthly_summary.csv, assets.csv, and liabilities.csv.
 * Shows validation errors and parse results.
 */

import { useState, useRef, DragEvent } from 'react';

interface Props {
  onUpload: (files: File[]) => Promise<void>;
  parsing: boolean;
  errors: string[];
}

export default function CSVUploader({ onUpload, parsing, errors }: Props) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files).filter((f) => f.name.endsWith('.csv'));
    if (files.length > 0) onUpload(files);
  }

  function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) onUpload(files);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? 'var(--color-accent)' : 'var(--color-border)'}`,
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-xl)',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragging ? '#fffaf0' : '#fafafa',
          transition: 'all 0.2s',
        }}
      >
        <div style={{ fontSize: '2rem', marginBottom: 8 }}>📂</div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {parsing ? 'Parsing...' : 'Drop CSV files here or click to upload'}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
          Accepts: monthly_summary.csv · assets.csv · liabilities.csv
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          multiple
          onChange={handleFiles}
          style={{ display: 'none' }}
        />
      </div>

      {errors.length > 0 && (
        <div style={{ marginTop: 'var(--spacing-md)', padding: 'var(--spacing-md)', background: '#fff5f5', borderRadius: 'var(--radius-md)', border: '1px solid #fed7d7' }}>
          <div style={{ fontWeight: 600, color: 'var(--color-error)', marginBottom: 6 }}>Parse warnings:</div>
          {errors.map((err, i) => (
            <div key={i} style={{ fontSize: '0.8rem', color: 'var(--color-error)' }}>• {err}</div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 'var(--spacing-md)', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
        <strong>CSV schema:</strong> Export your Google Sheet tabs individually. Column names must match exactly.
        <br />
        monthly_summary: month, earned_income, passive_income, portfolio_income, tax_expenses, housing_expenses, living_expenses, debt_expenses, other_expenses
        <br />
        assets: month, name, category, current_value, monthly_cashflow
        <br />
        liabilities: month, name, category, outstanding_balance, monthly_payment, interest_rate
      </div>
    </div>
  );
}
